// MobileMoneyBalances.tsx — inner content for the Mobile Money tab of /balances
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import {
  Wallet, RefreshCw, Clock, Info, ArrowUpRight, ArrowDownLeft,
  ChevronRight, Activity,
} from 'lucide-react';
import { dashboardService, DashboardStats } from '../services/dashboardService';
import { getMerchantBalances } from '../services/transferService';
import WalletDetailsModal, { WalletDetailsData } from './dashboard/WalletDetailsModal';

interface OperatorBalance {
  method: string;
  currency: string;
  depositBalance: number;
  withdrawalBalance: number;
  reservedDeposit: number;
  reservedWithdrawal: number;
  available: number;
  inFlight: number;
  total: number;
}

const OPERATOR_LOGO: Record<string, string> = {
  'Orange':    '/orange.png',
  'Airtel':    '/airtel.png',
  'M-Pesa':    '/mpesa.png',
  'Afrimoney': '/afrimoney.png',
};

const OPERATOR_COLOR: Record<string, string> = {
  'Orange':    '#FF8C00',
  'Airtel':    '#E53E3E',
  'M-Pesa':    '#00A651',
  'Afrimoney': '#6B46C1',
  'MTN':       '#FFCC00',
  'Vodacom':   '#E60000',
  'Moov':      '#00A651',
  'Safaricom': '#00A651',
  'Telkom':    '#0066CC',
  'Free':      '#C41E3A',
  'Expresso':  '#FF6600',
  'Malitel':   '#0066CC',
  'Vodafone':  '#E60000',
  'AirtelTigo':'#E53E3E',
  'Africell':  '#00A651',
  'Tigo':      '#0066CC',
  'Halotel':   '#FF6600',
  'Telecel':   '#0066CC',
};

const MobileMoneyBalances: React.FC = () => {
  const { user } = useAuth();
  const { t, countryConfig } = useLocalization();
  const [selectedCurrency, setSelectedCurrency] = useState('CDF');
  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState<OperatorBalance[]>([]);
  const [, setDashboardData] = useState<DashboardStats | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [showDetails, setShowDetails] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState<OperatorBalance | null>(null);

  const currencies = ['CDF', 'USD'];

  const loadBalances = async () => {
    if (!user?.merchant_code) return;

    try {
      setLoading(true);

      const [stats] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getOperatorStats()
      ]);

      setDashboardData(stats);

      const operatorBalances: OperatorBalance[] = [];

      for (const operator of countryConfig.operators) {
        const normalizedMethod = operator.toLowerCase() === 'm-pesa' ? 'mpesa' : operator.toLowerCase();

        try {
          const balanceData = await getMerchantBalances(
            user.merchant_code,
            selectedCurrency,
            normalizedMethod
          );

          operatorBalances.push({
            method: operator,
            currency: selectedCurrency,
            depositBalance: balanceData.deposit_balance || 0,
            withdrawalBalance: balanceData.withdrawal_balance || 0,
            reservedDeposit: balanceData.reserved_deposit || 0,
            reservedWithdrawal: balanceData.reserved_withdrawal || 0,
            available: balanceData.available || 0,
            inFlight: balanceData.in_flight || 0,
            total: balanceData.total_balance || 0,
          });
        } catch (error) {
          console.warn(`Failed to load balance for ${operator} (${selectedCurrency}):`, error);
          operatorBalances.push({
            method: operator,
            currency: selectedCurrency,
            depositBalance: 0,
            withdrawalBalance: 0,
            reservedDeposit: 0,
            reservedWithdrawal: 0,
            available: 0,
            inFlight: 0,
            total: 0,
          });
        }
      }

      setBalances(operatorBalances);
      setLastUpdated(new Date());

    } catch (error) {
      console.error('Error loading balances:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBalances();
  }, [user?.merchant_code, selectedCurrency]);

  const totalAvailable = balances.reduce((sum, b) => sum + b.available, 0);
  const totalInFlight = balances.reduce((sum, b) => sum + b.inFlight, 0);
  const totalAll = balances.reduce((sum, b) => sum + b.total, 0);

  const fmt = (n: number) =>
    new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const fmtTime = (d: Date) =>
    d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="space-y-5">

      {/* Toolbar — currency switch + live indicator + refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {!loading && (
            <>
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              <span className="text-emerald-600 font-semibold">Live</span>
              <span className="text-slate-400">· {fmtTime(lastUpdated)}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Currency tabs — matches the rest of the app */}
          <div className="inline-flex items-center bg-slate-100 ring-1 ring-slate-200 rounded-lg p-0.5">
            {currencies.map((c) => (
              <button
                key={c}
                onClick={() => setSelectedCurrency(c)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  selectedCurrency === c
                    ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/30'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <button
            onClick={loadBalances}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-xs font-semibold disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Totals row — primary hero + secondary cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Primary: Available */}
        <div className="lg:col-span-1 bg-slate-900 ring-1 ring-emerald-400/25 rounded-2xl p-5 text-white relative overflow-hidden shadow-lg shadow-emerald-500/10">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-emerald-500/20 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em]">
                {t('balances.available') || 'Disponible'}
              </span>
              <div className="h-9 w-9 rounded-xl bg-emerald-500/15 ring-1 ring-emerald-400/30 flex items-center justify-center">
                <Wallet className="h-4 w-4 text-emerald-300" />
              </div>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-[28px] font-bold leading-none tracking-tight text-emerald-300 tabular-nums">
                {loading ? '—' : fmt(totalAvailable)}
              </span>
              <span className="text-xs font-medium text-slate-500">{selectedCurrency}</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">{t('balances.availableHint') || 'Utilisable maintenant'}</p>
          </div>
        </div>

        {/* Reserved */}
        <KpiCard
          label={t('balances.inFlight') || 'Réservé'}
          value={loading ? '—' : fmt(totalInFlight)}
          currency={selectedCurrency}
          hint={t('balances.inFlightHint') || 'Bloqué pour des paiements en cours'}
          icon={<Clock className="h-3.5 w-3.5" />}
          accent="amber"
        />

        {/* Total */}
        <KpiCard
          label={t('balances.total') || 'Total'}
          value={loading ? '—' : fmt(totalAll)}
          currency={selectedCurrency}
          hint={t('balances.totalHint') || 'Disponible + Réservé'}
          icon={<Activity className="h-3.5 w-3.5" />}
          accent="gray"
        />
      </div>

      {/* Operator wallets */}
      <div className="bg-slate-900 rounded-2xl ring-1 ring-white/[0.06] overflow-hidden shadow-lg shadow-black/20">
        {/* Section header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-sm font-semibold text-white">{t('balances.operators')}</h2>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {t('balances.subtitle')} <span className="text-slate-500">— {selectedCurrency}</span>
            </p>
          </div>
          <button
            onClick={() => setShowDetails((v) => !v)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-slate-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 rounded-lg transition-colors"
          >
            <Info className="h-3.5 w-3.5" />
            {showDetails ? (t('balances.hideDetails') || 'Masquer le détail') : (t('balances.showDetails') || 'Voir le détail')}
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {balances.map((balance, index) => (
                <OperatorCard
                  key={index}
                  balance={balance}
                  totalAvailable={totalAvailable}
                  showDetails={showDetails}
                  fmt={fmt}
                  onClick={() => setSelectedBalance(balance)}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <WalletDetailsModal
        data={
          selectedBalance
            ? ({
                name: selectedBalance.method,
                currency: selectedBalance.currency,
                deposit: selectedBalance.depositBalance,
                withdrawal: selectedBalance.withdrawalBalance,
                reservedDeposit: selectedBalance.reservedDeposit,
                reservedWithdrawal: selectedBalance.reservedWithdrawal,
                available: selectedBalance.available,
                inFlight: selectedBalance.inFlight,
                total: selectedBalance.total,
                iconColor: OPERATOR_COLOR[selectedBalance.method] || '#6B46C1',
              } as WalletDetailsData)
            : null
        }
        onClose={() => setSelectedBalance(null)}
      />
    </div>
  );
};

/* ───────── Sub-components ───────── */

function KpiCard({
  label, value, currency, hint, icon, accent,
}: {
  label: string;
  value: string;
  currency: string;
  hint: string;
  icon: React.ReactNode;
  accent: 'amber' | 'gray' | 'emerald';
}) {
  const accents: Record<string, { iconBg: string; iconColor: string; valueColor: string; ring: string; blur: string }> = {
    amber:   { iconBg: 'bg-amber-500/15',   iconColor: 'text-amber-300',   valueColor: 'text-amber-200',   ring: 'ring-amber-400/25',   blur: 'bg-amber-500/15' },
    gray:    { iconBg: 'bg-white/5',         iconColor: 'text-slate-300',   valueColor: 'text-white',       ring: 'ring-white/[0.06]',    blur: 'bg-slate-500/10' },
    emerald: { iconBg: 'bg-emerald-500/15', iconColor: 'text-emerald-300', valueColor: 'text-emerald-300', ring: 'ring-emerald-400/25', blur: 'bg-emerald-500/15' },
  };
  const a = accents[accent];
  return (
    <div className={`relative bg-slate-900 rounded-2xl ring-1 ${a.ring} p-5 overflow-hidden shadow-lg shadow-black/20`}>
      <div className={`absolute -top-12 -right-12 w-48 h-48 ${a.blur} rounded-full blur-3xl pointer-events-none`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em]">{label}</span>
          <div className={`h-9 w-9 rounded-xl ${a.iconBg} ${a.iconColor} ring-1 ${a.ring} flex items-center justify-center`}>
            {icon}
          </div>
        </div>
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-[28px] font-bold leading-none tracking-tight tabular-nums ${a.valueColor}`}>{value}</span>
          <span className="text-xs font-medium text-slate-500">{currency}</span>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">{hint}</p>
      </div>
    </div>
  );
}

function OperatorCard({
  balance, totalAvailable, showDetails, fmt, onClick, t,
}: {
  balance: OperatorBalance;
  totalAvailable: number;
  showDetails: boolean;
  fmt: (n: number) => string;
  onClick: () => void;
  t: (k: string) => string;
}) {
  const logo = OPERATOR_LOGO[balance.method];
  const color = OPERATOR_COLOR[balance.method] || '#6B46C1';
  const sharePct = totalAvailable > 0 ? Math.min(100, (balance.available / totalAvailable) * 100) : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative text-left w-full rounded-xl ring-1 ring-white/[0.06] bg-slate-900 p-4 hover:ring-white/15 hover:bg-slate-900/80 transition-all group overflow-hidden"
    >
      {/* Operator color stripe (top) */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ backgroundColor: color }}
      />

      {/* Top row: logo + name + currency */}
      <div className="flex items-center justify-between mb-3 mt-0.5">
        <div className="flex items-center gap-2.5 min-w-0">
          {logo ? (
            <div className="h-9 w-9 rounded-lg bg-white/5 ring-1 ring-white/10 flex items-center justify-center overflow-hidden shrink-0">
              <img src={logo} alt={balance.method} className="h-6 w-6 object-contain" />
            </div>
          ) : (
            <div
              className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 text-white text-xs font-bold"
              style={{ backgroundColor: color }}
            >
              {balance.method.charAt(0)}
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white truncate capitalize">{balance.method}</h3>
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{balance.currency}</span>
          </div>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-slate-500 group-hover:text-slate-300 group-hover:translate-x-0.5 transition-all shrink-0" />
      </div>

      {/* Available — primary metric */}
      <div className="mb-3">
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
          {t('balances.available') || 'Disponible'}
        </p>
        <p className="text-lg font-bold text-white tracking-tight tabular-nums">{fmt(balance.available)}</p>
      </div>

      {/* Share bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-slate-500">{t('balances.shareOfTotal') || 'Part du total'}</span>
          <span className="text-slate-300 font-semibold tabular-nums">{sharePct.toFixed(1)}%</span>
        </div>
        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${sharePct}%`, backgroundColor: color }}
          />
        </div>
      </div>

      {/* Reserved chip */}
      {balance.inFlight > 0 && (
        <div className="inline-flex items-center gap-1.5 text-[11px] text-amber-200 bg-amber-500/15 ring-1 ring-amber-400/30 rounded px-1.5 py-0.5 mb-2">
          <Clock className="h-2.5 w-2.5" />
          <span className="font-semibold tabular-nums">{fmt(balance.inFlight)}</span>
          <span className="opacity-75">{t('balances.reserved') || 'réservé'}</span>
        </div>
      )}

      {/* Details */}
      {showDetails && (
        <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-1.5">
          <DetailRow
            label="Pay-In"
            value={fmt(balance.depositBalance)}
            icon={<ArrowDownLeft className="h-2.5 w-2.5" />}
            tone="emerald"
          />
          <DetailRow
            label="Pay-Out"
            value={fmt(balance.withdrawalBalance)}
            icon={<ArrowUpRight className="h-2.5 w-2.5" />}
            tone="sky"
          />
          {(balance.reservedDeposit > 0 || balance.reservedWithdrawal > 0) && (
            <DetailRow
              label={t('balances.reserved') || 'Réservé'}
              value={fmt(balance.reservedDeposit + balance.reservedWithdrawal)}
              icon={<Clock className="h-2.5 w-2.5" />}
              tone="amber"
            />
          )}
          <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-white/[0.06] text-[11px]">
            <span className="font-medium text-slate-400">{t('balances.total') || 'Total'}</span>
            <span className="font-bold text-white tabular-nums">{fmt(balance.total)}</span>
          </div>
        </div>
      )}
    </button>
  );
}

function DetailRow({
  label, value, icon, tone,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: 'emerald' | 'sky' | 'amber';
}) {
  const tones: Record<string, string> = {
    emerald: 'text-emerald-300',
    sky:     'text-sky-300',
    amber:   'text-amber-300',
  };
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="flex items-center gap-1.5 text-slate-400">
        <span className={tones[tone]}>{icon}</span>
        {label}
      </span>
      <span className="font-semibold text-slate-200 tabular-nums">{value}</span>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-xl ring-1 ring-white/[0.06] bg-slate-900 p-4 animate-pulse">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="h-9 w-9 rounded-lg bg-white/5" />
        <div className="space-y-1.5 flex-1">
          <div className="h-3 bg-white/5 rounded w-1/2" />
          <div className="h-2 bg-white/5 rounded w-1/4" />
        </div>
      </div>
      <div className="h-2 bg-white/5 rounded w-1/4 mb-1.5" />
      <div className="h-5 bg-white/5 rounded w-3/5 mb-3" />
      <div className="h-1.5 bg-white/5 rounded w-full" />
    </div>
  );
}

export default MobileMoneyBalances;
